import React, { cloneElement, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';

import { ResetPortfolioItemsProgressDocument } from 'graphql/types';
import { useApolloError } from 'common/hooks/useApolloError';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { ConfirmDialog } from 'common/components/ConfirmDialog';

interface IProps {
    portfolioItemIds: string[];
    onSuccess?(): void;
    children?: React.ReactNode;
    skipCacheFieldReset?: boolean;
    translationKey?:
        | 'resetTrainingPortfolioItemProgress'
        | 'resetPortfolioItemProgress';
}

export const ResetPortfolioItemProgressAction = ({
    portfolioItemIds,
    onSuccess,
    children,
    skipCacheFieldReset,
    translationKey = 'resetPortfolioItemProgress',
}: IProps) => {
    const [translate] = useTranslation();
    const { showApolloError } = useApolloError();
    const [displaySnackbar] = useSnackbar();

    const [showResetProgressDialog, setShowResetProgressDialog] =
        useState(false);

    const [resetProgress, { loading: loadingResetProgress }] = useMutation(
        ResetPortfolioItemsProgressDocument
    );

    const handleResetProgress = async () => {
        let response;

        try {
            response = await resetProgress({
                variables: {
                    portfolioItemIds: portfolioItemIds,
                },
                update: (cache, { data }) => {
                    if (skipCacheFieldReset) return;

                    const { succeededIds = [] } =
                        data?.resetPortfolioItemsProgress || {};

                    const newFieldData = {
                        progress: () => 0,
                        score: () => 0,
                        startDate: () => new Date().toISOString(),
                        completionDate: () => null,
                        timeSpent: () => null,
                    };

                    // Clear portfolio item data after reset
                    succeededIds.forEach((id) => {
                        cache.modify({
                            id: cache.identify({
                                __typename: 'PortfolioItem',
                                id,
                            }),
                            fields: newFieldData,
                        });
                        cache.modify({
                            id: cache.identify({
                                __typename: 'PortfolioItemReport',
                                id,
                            }),
                            fields: newFieldData,
                        });
                    });

                    cache.gc();
                },
            });
        } catch (error) {
            showApolloError(error);

            return;
        }

        const { data } = response;
        const { succeededIds, failedIds } =
            data?.resetPortfolioItemsProgress || {};

        if (!!succeededIds?.length) {
            let messageTransKey = `${translationKey}.progressResetSuccess`;

            if (!failedIds?.length) {
                // If no failed ids, show success message for all
                messageTransKey = `${translationKey}.progressResetSuccessAll`;
            }

            displaySnackbar(
                translate(messageTransKey, {
                    count: succeededIds.length,
                }),
                {
                    variant: 'success',
                }
            );
        }

        if (failedIds?.length) {
            displaySnackbar(
                translate(`${translationKey}.progressResetFailed`, {
                    count: failedIds.length,
                }),
                {
                    variant: 'error',
                }
            );
        }

        setShowResetProgressDialog(false);
        onSuccess?.();
    };

    return (
        <>
            {React.Children.map(children, (child) => {
                if (!React.isValidElement(child)) return null;

                return cloneElement(
                    child as React.ReactElement<{
                        onClick?: () => void;
                        onKeyDown?: (
                            e: React.KeyboardEvent<HTMLButtonElement>
                        ) => void;
                    }>,
                    {
                        onClick: () => setShowResetProgressDialog(true),
                        onKeyDown: (
                            e: React.KeyboardEvent<HTMLButtonElement>
                        ) => {
                            if (e.key !== 'Enter') return;

                            setShowResetProgressDialog(true);

                            e.preventDefault();
                        },
                    }
                );
            })}

            <ConfirmDialog
                confirmText={translate(`${translationKey}.dialog.confirmText`)}
                loading={loadingResetProgress}
                open={showResetProgressDialog}
                title={translate(`${translationKey}.dialog.title`)}
                onCancel={() => {
                    setShowResetProgressDialog(false);
                }}
                onClose={() => setShowResetProgressDialog(false)}
                onConfirm={handleResetProgress}
            >
                {translate(`${translationKey}.dialog.text`, {
                    count: portfolioItemIds.length,
                })}
            </ConfirmDialog>
        </>
    );
};
